import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@core/config/env';
import { SESSION_KEYS } from '@core/auth/session';
import { requestContext } from './requestContext';
import { isEmbeddedWidget } from './surface';
import {
  ApiError,
  AuthError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  ValidationError,
  NetworkError,
} from './types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateRequestId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * @deprecated Use ContextStore.setActiveAgent() instead.
 * Kept for backward compatibility with embed / widget code.
 */
export function setActiveAgent(agentId: string | null) {
  requestContext.setAgentId(agentId);
}

export function getActiveAgent(): string | null {
  return requestContext.getAgentId();
}

// ── Axios instance ────────────────────────────────────────────────────────────

const client: AxiosInstance = axios.create({
  baseURL: `${env.API_BASE_URL}${env.API_PREFIX}`,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  // Send the httpOnly refresh cookie on auth calls.
  withCredentials: true,
});

// ── Access-token refresh (single-flight) ──────────────────────────────────────
// The refresh token lives in an httpOnly cookie; we POST /auth/refresh to mint a
// new access token. Concurrent 401s share one in-flight refresh.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<{ accessToken?: string }>(
        `${env.API_BASE_URL}${env.API_PREFIX}/auth/refresh`,
        null,
        { withCredentials: true },
      )
      .then((res) => {
        const token = res.data?.accessToken ?? null;
        if (token) localStorage.setItem(SESSION_KEYS.AUTH_TOKEN, token);
        return token;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * True on the public widget surface (no admin user session): the hosted
 * `/widget` pages, and the widget embedded on any customer's website —
 * whose address is the host site's, never `/widget`.
 */
function isPublicWidgetPage(): boolean {
  if (isEmbeddedWidget()) return true;
  return (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/widget')
  );
}

function redirectToLogin(): void {
  // Public widget pages authenticate via publishable key, not a user session —
  // a 401 there must never wipe the session or hijack the visitor to the admin
  // login (e.g. on refresh). Leave them on the widget page.
  if (isPublicWidgetPage()) return;

  Object.values(SESSION_KEYS).forEach((k) => localStorage.removeItem(k));
  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/login')
  ) {
    const from = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.assign(`/login?from=${from}`);
  }
}

// ── Request interceptor ───────────────────────────────────────────────────────

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // ── Auth token ──────────────────────────────────────────────────────────
    const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // ── Tenant context ──────────────────────────────────────────────────────
    // Read from requestContext (written by ContextStore / auth store).
    // This is the canonical source — localStorage is only used for the token.
    const tenantId = requestContext.getTenantId();
    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    } else {
      // Fallback: read from localStorage for backward compat (e.g. widget)
      const storedTenantId = localStorage.getItem(SESSION_KEYS.TENANT_ID);
      if (storedTenantId) config.headers['x-tenant-id'] = storedTenantId;
    }

    // ── User context ────────────────────────────────────────────────────────
    const userId = localStorage.getItem(SESSION_KEYS.USER_ID);
    if (userId) config.headers['x-user-id'] = userId;

    // ── Agent context ───────────────────────────────────────────────────────
    const agentId = requestContext.getAgentId();
    if (agentId) config.headers['x-agent-id'] = agentId;

    // Distributed tracing — lets you correlate FE requests with BE logs
    config.headers['x-request-id'] = generateRequestId();
    config.headers['x-client-version'] = env.APP_ENV;

    if (env.isDev) {
      const method = config.method?.toUpperCase().padEnd(6);
      console.debug(`[API →] ${method} ${config.baseURL}${config.url}`);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor ──────────────────────────────────────────────────────

client.interceptors.response.use(
  (response: AxiosResponse) => {
    if (env.isDev) {
      const method = response.config.method?.toUpperCase().padEnd(6);
      console.debug(
        `[API ←] ${method} ${response.config.url} ${response.status}`,
      );
    }
    return response;
  },
  async (error) => {
    const status: number = error.response?.status ?? 0;
    const data = error.response?.data ?? {};
    const message: string =
      data.message ?? error.message ?? 'An unexpected error occurred';
    const code: string = data.code ?? 'UNKNOWN_ERROR';

    if (env.isDev) {
      console.error(`[API ✗] ${error.config?.url} ${status}`, data);
    }

    // ── 401: try a one-time refresh, then retry; else send to login ─────────
    if (status === 401) {
      const cfg = error.config as
        | (InternalAxiosRequestConfig & { _authRetry?: boolean; url?: string })
        | undefined;
      const isAuthCall = cfg?.url?.includes('/auth/');

      // Auth calls (login / refresh) failing are handled by the caller
      // (LoginPage shows the error; AuthBootstrap stays unauthenticated).
      if (!isAuthCall) {
        if (!isPublicWidgetPage() && cfg && !cfg._authRetry) {
          cfg._authRetry = true;
          const token = await refreshAccessToken();
          if (token) {
            cfg.headers = cfg.headers ?? {};
            (cfg.headers as Record<string, string>).Authorization =
              `Bearer ${token}`;
            return client(cfg);
          }
        }
        redirectToLogin();
      }
      return Promise.reject(new AuthError(message, data));
    }

    // ── Map HTTP status → typed error ──────────────────────────────────────
    if (status === 400) {
      return Promise.reject(
        new ValidationError(message, data.errors ?? {}, data),
      );
    }
    if (status === 403) return Promise.reject(new ForbiddenError(message, data));
    if (status === 404) return Promise.reject(new NotFoundError(message, data));
    if (status === 409) return Promise.reject(new ConflictError(message, data));

    // No HTTP response — true network / transport failure.
    // Never auto-retry non-idempotent auth POSTs (e.g. /auth/mfa/verify): the
    // MFA challenge token is single-use, so a timed-out request that still
    // completed on the server makes a retry look like "code expired".
    if (status === 0) {
      const cfg = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
        url?: string;
        method?: string;
      };
      const method = (cfg.method ?? 'get').toLowerCase();
      const isAuthMutation =
        Boolean(cfg.url?.includes('/auth/')) &&
        method !== 'get' &&
        method !== 'head';
      if (!isAuthMutation && !cfg._retry) {
        cfg._retry = true;
        await sleep(800);
        return client(cfg);
      }
      return Promise.reject(new NetworkError(message, data));
    }

    // Server responded with an error — backend is reachable, request failed server-side
    if (status >= 500) {
      const cfg = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
        url?: string;
        method?: string;
      };
      const method = (cfg.method ?? 'get').toLowerCase();
      const isAuthMutation =
        Boolean(cfg.url?.includes('/auth/')) &&
        method !== 'get' &&
        method !== 'head';
      if (!isAuthMutation && !cfg._retry && status === 503) {
        cfg._retry = true;
        await sleep(800);
        return client(cfg);
      }
      return Promise.reject(new ApiError(status, code, message, data));
    }

    // Fallback for any unhandled status
    return Promise.reject(new ApiError(status, code, message, data));
  },
);

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default client;

/**
 * Override the API base URL at runtime.
 * Used by the widget embed to point at the correct backend when the
 * compiled-in VITE_API_BASE_URL doesn't match the deployment environment.
 */
export function setApiBaseUrl(baseUrl: string): void {
  const prefix = env.API_PREFIX ?? '/api/v1';
  client.defaults.baseURL = `${baseUrl.replace(/\/$/, '')}${prefix}`;
}

// ── Typed request helpers ─────────────────────────────────────────────────────

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await client.get<T>(url, config);
  return res.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.post<T>(url, data, config);
  return res.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.put<T>(url, data, config);
  return res.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await client.patch<T>(url, data, config);
  return res.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await client.delete<T>(url, config);
  return res.data;
}

/**
 * Multipart file upload with progress tracking.
 * @param onProgress Called with 0–100 as the upload progresses.
 */
export async function upload<T>(
  url: string,
  formData: FormData,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  const res = await client.post<T>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Knowledge upload waits for parse before responding; keep above the
    // default 30s so large PDFs don't look "stuck" then fail/retry.
    timeout: 5 * 60_000,
    // Never auto-retry uploads — the server may already have created the row.
    _retry: true,
    signal,
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  } as AxiosRequestConfig & { _retry?: boolean });
  return res.data;
}
